<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <main class="p-4 pt-0">
        <article>
          <RouterView />
        </article>
      </main>
    </template>
  </VaLayout>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, onUnmounted, watch, ref, computed } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBreakpoint } from 'vuestic-ui'
import { useGlobalStore } from '../stores/global-store'
import { firebaseAuth, firestore } from '../firebase'
import { doc, collection, onSnapshot, query, Unsubscribe } from 'firebase/firestore'
import { getDateStringFromTimestamp, getUserName } from '../functions'
import { COA, DailyStats, Order, Product, PromoCode, ShippingFee, Stats, User } from '../types'

import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'

const GlobalStore = useGlobalStore()

const breakpoints = useBreakpoint()

const route = useRoute()
const { replace } = useRouter()

const { isMobile, isTablet, isSidebarMinimized, statsInfo, dailyStatsList, userList, productList, promoCodeList, shippingFeeList, orderList, coaList } = storeToRefs(GlobalStore)

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref('')

const snapStatsInfo = ref<Unsubscribe | null>(null)
const snapDailyStatsList = ref<Unsubscribe | null>(null)
const snapUserList = ref<Unsubscribe | null>(null)
const snapProductList = ref<Unsubscribe | null>(null)
const snapPromoCodeList = ref<Unsubscribe | null>(null)
const snapShippingFeeList = ref<Unsubscribe | null>(null)
const snapOrderList = ref<Unsubscribe | null>(null)
const snapCOAList = ref<Unsubscribe | null>(null)

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

watch(() => route.fullPath, loadAllData, { immediate: true })

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
  firebaseAuth.onAuthStateChanged((user) => {
    if (user && user.uid === 'GrnUenIy61UHcTHz4G4bvRhp2gA3') {
      loadAllData()
    } else {
      replace('/auth/login')
    }
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onUnmounted(() => {
  if (snapStatsInfo.value) {
    snapStatsInfo.value()
    snapStatsInfo.value = null
  }
  if (snapDailyStatsList.value) {
    snapDailyStatsList.value()
    snapDailyStatsList.value = null
  }
  if (snapUserList.value) {
    snapUserList.value()
    snapUserList.value = null
  }
  if (snapProductList.value) {
    snapProductList.value()
    snapProductList.value = null
  }
  if (snapPromoCodeList.value) {
    snapPromoCodeList.value()
    snapPromoCodeList.value = null
  }
  if (snapShippingFeeList.value) {
    snapShippingFeeList.value()
    snapShippingFeeList.value = null
  }
  if (snapOrderList.value) {
    snapOrderList.value()
    snapOrderList.value = null
  }
  if (snapCOAList.value) {
    snapCOAList.value()
    snapCOAList.value = null
  }
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

function onResize() {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

function onCloseSidebarButtonClick() {
  isSidebarMinimized.value = true
}

function loadAllData() {
  if (route.fullPath === '/dashboard') {
    getStatsInfoTask()
    getDailyStatsListTask()
  }
  if (route.fullPath.startsWith('/products')) {
    getProductListTask()
  }
  if (route.fullPath.startsWith('/promocodes')) {
    getPromoCodeListTask()
  }
  if (route.fullPath === '/shipping-fee') {
    getShippingFeeListTask()
  }
  if (route.fullPath === '/users') {
    getUserListTask()
  }
  if (route.fullPath.startsWith('/orders')) {
    getOrderListTask()
  }
  if (route.fullPath === '/coas') {
    getCOAListTask()
  }
}

function getStatsInfoTask() {
  if (snapStatsInfo.value) {
    return
  }
  snapStatsInfo.value = onSnapshot(doc(firestore, 'stats', 'admin'), doc => {
    statsInfo.value = doc.data() as Stats
  })
}

function getDailyStatsListTask() {
  if (snapDailyStatsList.value) {
    return
  }
  const q = query(collection(firestore, 'stats', 'admin', 'daily'))
  snapDailyStatsList.value = onSnapshot(q, querySnapshot => {
    const list: DailyStats[] = []
    querySnapshot.forEach(doc => {
      const data = doc.data()
      const info: DailyStats = {
        date: doc.id,
        users: data.users || 0,
        orders: data.orders || 0,
        ordersByPromoCode: data.ordersByPromoCode || 0,
        earnings: data.earnings || 0
      }
      list.push(info)
    })
    dailyStatsList.value = list
  })
}

function getUserListTask() {
  if (snapUserList.value) {
    return
  }
  const q = query(collection(firestore, 'user'))
  snapUserList.value = onSnapshot(q, querySnapshot => {
    const list: User[] = []
    querySnapshot.forEach(doc => {
      const data = doc.data()
      const info: User = {
        userId: data.userId,
        userName: getUserName(data.firstName, data.lastName),
        email: data.email,
        phone: data.phone,
        orderCount: data.orderCount || 0,
        lastLoginAt: getDateStringFromTimestamp(data.lastLoginAt),
        createdAt: getDateStringFromTimestamp(data.createdAt)
      }
      list.push(info)
    })
    userList.value = list
  })
}

function getProductListTask() {
  if (snapProductList.value) {
    return
  }
  const q = query(collection(firestore, 'product'))
  snapProductList.value = onSnapshot(q, querySnapshot => {
    const list: Product[] = []
    querySnapshot.forEach(doc => {
      const data = doc.data()
      const info: Product = {
        productId: data.productId,
        name: data.name,
        category: data.category,
        ingredients: data.ingredients,
        description: data.description,
        thumbnailUrl: data.thumbnailUrl,
        imageUrls: data.imageUrls,
        prices: data.prices,
        minPrice: 0,
        hasSubscription: data.discountPercent > 0,
        discountPercent: data.discountPercent || 0,
        isVisible: data.isVisible,
        orderCount: data.orderCount || 0,
        createdAt: getDateStringFromTimestamp(data.createdAt),
        updatedAt: getDateStringFromTimestamp(data.updatedAt)
      }
      info.minPrice = Math.min(...info.prices.map(p => p.price))
      list.push(info)
    })
    productList.value = list
  })
}

function getPromoCodeListTask() {
  if (snapPromoCodeList.value) {
    return
  }
  const q = query(collection(firestore, 'promocode'))
  snapPromoCodeList.value = onSnapshot(q, querySnapshot => {
    const list: PromoCode[] = []
    querySnapshot.forEach(doc => {
      const data = doc.data()
      const info: PromoCode = {
        code: data.code,
        promoPercent: data.promoPercent,
        isActive: data.isActive,
        totalQuantity: data.totalQuantity || 0,
        usedQuantity: data.usedQuantity || 0,
        createdAt: getDateStringFromTimestamp(data.createdAt),
        updatedAt: getDateStringFromTimestamp(data.updatedAt)
      }
      list.push(info)
    })
    promoCodeList.value = list
  })
}

function getShippingFeeListTask() {
  if (snapShippingFeeList.value) {
    return
  }
  const q = query(collection(firestore, 'shippingFee'))
  snapShippingFeeList.value = onSnapshot(q, querySnapshot => {
    const list: ShippingFee[] = []
    querySnapshot.forEach(doc => {
      const data = doc.data()
      const info: ShippingFee = {
        shippingFeeId: data.shippingFeeId,
        title: data.title,
        price: data.price,
        freeOver: data.freeOver,
        createdAt: getDateStringFromTimestamp(data.createdAt),
        updatedAt: getDateStringFromTimestamp(data.updatedAt)
      }
      list.push(info)
    })
    list.sort((a, b) => a.price > b.price ? 1 : -1)
    shippingFeeList.value = list
  })
}

function getOrderListTask() {
  if (snapOrderList.value) {
    return
  }
  const q = query(collection(firestore, 'order'))
  snapOrderList.value = onSnapshot(q, querySnapshot => {
    const list: Order[] = []
    querySnapshot.forEach(doc => {
      const data = doc.data()
      const info: Order = {
        orderId: data.orderId,
        orderNumber: data.orderNumber,
        userId: data.userId,
        userName: getUserName(data.firstName, data.lastName),
        email: data.email,
        phone: data.phone,
        address: data.address,
        geoPoint: data.geoPoint,
        taxPrice: data.taxPrice,
        totalPrice: data.totalPrice,
        shippingTitle: data.shippingTitle,
        shippingFee: data.shippingFee,
        promoCode: data.promoCode,
        promoPercent: data.promoPercent,
        details: data.details,
        status: data.status,
        note: data.note,
        createdAt: getDateStringFromTimestamp(data.createdAt),
        updatedAt: getDateStringFromTimestamp(data.updatedAt),
      }
      list.push(info)
    })
    orderList.value = list
  })
}

function getCOAListTask() {
  if (snapCOAList.value) {
    return
  }
  const q = query(collection(firestore, 'coa'))
  snapCOAList.value = onSnapshot(q, querySnapshot => {
    const list: COA[] = []
    querySnapshot.forEach(doc => {
      const data = doc.data()
      const info: COA = {
        id: data.id,
        url: data.url,
        createdAt: getDateStringFromTimestamp(data.createdAt),
      }
      list.push(info)
    })
    list.sort((a, b) => a.createdAt < b.createdAt ? 1 : -1)
    coaList.value = list
  })
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}
</style>